/* ═══════════════════════════════════════════════════════
   NYC Suite — Quote Attachment Storage (Firebase Storage)
   Durable, cross-device storage for staff-attached quote
   files. Scoped to quotes only — uploads live under
   quote-attachments/{quoteId}/. Firestore stores only the
   returned download url (never the file bytes); the bytes
   live in Cloud Storage. Image dataUrls are cached locally
   in IndexedDB separately (imageStorage.js).
   ═══════════════════════════════════════════════════════ */
import { storage, storageRef, uploadBytes, getDownloadURL, deleteObject } from "../firebase.js";

// Keep object names filesystem/URL-safe and bounded.
const safeName = (name) =>
  (name || "file").replace(/[^a-zA-Z0-9._-]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 120) || "file";

export const attachmentPath = (quoteId, attachmentId, fileName) =>
  `quote-attachments/${quoteId}/${attachmentId}-${safeName(fileName)}`;

// Upload one file to Cloud Storage and return its durable download url + path.
// Throws on failure so the caller can surface an error state on the attachment.
export async function uploadAttachment(quoteId, attachmentId, file) {
  const path = attachmentPath(quoteId, attachmentId, file.name);
  const r = storageRef(storage, path);
  await uploadBytes(r, file);
  const url = await getDownloadURL(r);
  return { url, path };
}

// Best-effort cleanup when an attachment is removed. Never throws — a missing
// object or a permissions hiccup must not break the remove interaction.
export async function deleteAttachmentFile(path) {
  if (!path) return;
  try {
    await deleteObject(storageRef(storage, path));
  } catch (e) {
    console.warn("[attachmentStorage] delete failed (ignored):", e?.code || e);
  }
}
